import { PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import {
  useMassClrRunPendingConfirmations,
  type MassClrLineConfirmationRow,
} from '@/hooks/queries/credit/usePendingConfirmations'
import { TooltipProvider } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CREDIT_ROUTES, toStaticData } from '../../../credit-route-config'
import {
  PendingConfirmationsTable,
  type PendingConfirmationRow,
} from '../../-components/PendingConfirmationsTable'

export const Route = createFileRoute(
  '/_auth/_modules/credit/credit-limit-reviews/mass/$runId/pending-confirmations',
)({
  staticData: toStaticData(
    CREDIT_ROUTES['/credit/credit-limit-reviews/mass/$runId/pending-confirmations'],
  ),
  beforeLoad: async ({ context }) => {
    context.handleRoutePermissions(Route)
  },
  component: RouteComponent,
})

function toRow(
  row: MassClrLineConfirmationRow,
  run: { runId: number; reference: string; approvedAt: string | null },
): PendingConfirmationRow {
  return {
    key: `mass:${row.runId}:${row.lineId}`,
    erp: row.erp,
    businessName: row.businessName,
    creditSegment: row.creditSegment,
    reviewLabel: run.reference,
    reviewTo: {
      to: '/credit/credit-limit-reviews/mass/$runId',
      params: { runId: String(run.runId) },
    },
    approvedAt: run.approvedAt,
    approvedCreditLimit: row.approvedCreditLimit,
    currentCreditLimit: row.currentCreditLimit,
    approvedRiskClass: row.approvedRiskClass,
    currentRiskClass: row.currentRiskClass,
    approvedNextReviewDate: row.approvedNextReviewDate,
    currentNextReviewDate: row.currentNextReviewDate,
    segmentUpdatedAt: row.segmentUpdatedAt,
    dispatch: row.dispatch,
    status: row.status,
  }
}

/**
 * Every applied account of one approved mass review against what its credit segment holds now —
 * confirmed ones included, so the list reads as the whole run. Read only; the run itself is on
 * its own page.
 */
function RouteComponent() {
  const { t } = useTranslation(['credit', 'common'])
  const { runId } = Route.useParams()
  const query = useMassClrRunPendingConfirmations(Number(runId))
  const run = query.data
  const rows = run ? run.results.map((row) => toRow(row, run)) : []

  return (
    <PageContainer maxWidth="wide">
      <Spread align="end">
        <VStack gap="xs">
          <PageHeader>
            {t('massClr.pendingConfirmations.runPageHeader', {
              reference: run?.reference ?? runId,
            })}
          </PageHeader>
          {run && (
            <Text tone="muted" className="max-w-3xl text-pretty">
              {run.name}
            </Text>
          )}
        </VStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <TooltipProvider delayDuration={150}>
            <PendingConfirmationsTable
              rows={rows}
              isPending={query.isPending}
              error={query.error}
            />
          </TooltipProvider>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
